/** @file hitGizmo: the gizmo handle under a screen point (a quad containing it, else the nearest line). */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import { screenOf } from '@zilo/pose3d-render-core/camera/screenOf';
import type { View } from '@zilo/pose3d-render-core/camera/viewOf';
import type { GizmoHandle, GizmoShape } from './gizmoShape';
import { segmentDistance } from './segmentDistance';

/** The smallest distance from `(x, y)` to a polyline projected through `view`. */
function lineDistance(line: readonly Vec3[], view: View, x: number, y: number): number {
  const pts = line.map((p) => screenOf(p, view));
  let best = Infinity;
  pts.forEach((b, i) => {
    const a = pts[i - 1];
    if (a !== undefined) best = Math.min(best, segmentDistance(x, y, a, b));
  });
  return best;
}

/** True when `(x, y)` lies inside the projected convex quad (either winding; an edge-on quad contains nothing). */
function quadContains(quad: readonly Vec3[], view: View, x: number, y: number): boolean {
  const pts = quad.map((p) => screenOf(p, view));
  let sign = 0;
  for (let i = 0; i < pts.length; i++) {
    const a = pts[i] ?? [0, 0];
    const b = pts[(i + 1) % pts.length] ?? [0, 0];
    const cross = (b[0] - a[0]) * (y - a[1]) - (b[1] - a[1]) * (x - a[0]);
    if (Math.abs(cross) < 1e-9) continue;
    if (sign !== 0 && Math.sign(cross) !== sign) return false;
    sign = Math.sign(cross);
  }
  return sign !== 0;
}

/**
 * The first handle (in `shapes` order) with a projected quad containing `(x, y)` (target pixels), else the handle
 * whose projected polylines pass nearest within `radiusPx`, else null.
 */
export function hitGizmo(shapes: readonly GizmoShape[], view: View, x: number, y: number, radiusPx = 3): GizmoHandle | null {
  const inQuad = shapes.find((s) => s.quads.some((q) => quadContains(q, view, x, y)));
  if (inQuad !== undefined) return inQuad.axis;
  let best: { axis: GizmoHandle; d: number } | null = null;
  for (const shape of shapes) {
    const d = Math.min(Infinity, ...shape.lines.map((line) => lineDistance(line, view, x, y)));
    if (d <= radiusPx && (best === null || d < best.d)) best = { axis: shape.axis, d };
  }
  return best?.axis ?? null;
}
